import React, { useEffect, useRef, useState } from "react";
import { Send, Bot, User } from "lucide-react";
import { useI18n } from "../../i18n";
import { api, API } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";

export default function AIAssistant() {
  const { t } = useI18n();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { api.get("/ai/history?session_id=default").then((r) => setMessages(r.data)).catch(() => {}); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    if (!input.trim() || streaming) return;
    const q = input.trim();
    setInput("");
    setMessages((m) => [...m, { role: "user", text: q }, { role: "assistant", text: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/ai/assistant`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: q, session_id: "default" }),
      });
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += dec.decode(value, { stream: true });
        setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", text: acc }; return c; });
      }
    } catch {
      setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", text: "—" }; return c; });
    } finally { setStreaming(false); }
  };

  return (
    <div className="max-w-3xl mx-auto flex flex-col h-[calc(100vh-8rem)] gc-fade-up">
      <div className="flex items-center gap-3 mb-4">
        <div className="h-10 w-10 rounded-xl bg-[hsl(217_63%_15%)] flex items-center justify-center"><Bot className="h-5 w-5 text-white" /></div>
        <div>
          <h1 className="font-display text-xl font-bold text-[hsl(217_63%_15%)]">{t("assistant_title")}</h1>
          <p className="text-xs text-muted-foreground">{t("assistant_hint")}</p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto gc-scroll rounded-2xl border border-border bg-card p-4 space-y-4">
        {messages.length === 0 && <div className="text-center text-muted-foreground text-sm py-12">{t("assistant_placeholder")}</div>}
        {messages.map((m, i) => (
          <div key={i} className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`} data-testid={`chat-msg-${i}`}>
            <div className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 ${m.role === "user" ? "bg-[hsl(158_64%_28%)]" : "bg-[hsl(217_63%_15%)]"}`}>
              {m.role === "user" ? <User className="h-4 w-4 text-white" /> : <Bot className="h-4 w-4 text-white" />}
            </div>
            <div className={`rounded-2xl px-4 py-2.5 text-sm max-w-[80%] whitespace-pre-wrap leading-relaxed ${m.role === "user" ? "bg-[hsl(158_64%_28%)] text-white" : "bg-secondary text-foreground"}`}>
              {m.text || (streaming && i === messages.length - 1 ? "…" : "")}
            </div>
          </div>
        ))}
        <div ref={endRef} />
      </div>

      <div className="mt-4 flex gap-2">
        <Input data-testid="chat-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder={t("assistant_placeholder")} className="h-12 rounded-full" />
        <Button data-testid="chat-send" onClick={send} disabled={streaming} className="h-12 w-12 rounded-full p-0 bg-[hsl(217_63%_15%)] hover:bg-[hsl(217_63%_20%)]">
          <Send className="h-5 w-5" />
        </Button>
      </div>
    </div>
  );
}
